import * as React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import { type ZoneRow } from './allureTable.ts';
import { allureAuPct, profilSeance, type Profil, type SegmentProfil } from './profilSeance.ts';
import { couleurPourCode } from '../Allures/couleursZones.tsx';

type PartiePlacee = { partie: string; debutS: number; finS: number };
type Repere = { code: string; pctVMA: number; nomZone: string };

/** Zones d'un effort (les deux zones d'un effort progressif "30'@HP-10K"). */
function zonesDe(s: SegmentProfil): Repere[] {
  if (!s.codeFin) return [{ code: s.code, pctVMA: s.pctVMA, nomZone: s.nomZone }];
  const [nomDebut, nomFin] = s.nomZone.split(' → ');
  return [
    { code: s.code, pctVMA: s.pctVMADebut ?? s.pctVMA, nomZone: nomDebut },
    { code: s.codeFin, pctVMA: s.pctVMAFin ?? s.pctVMA, nomZone: nomFin ?? nomDebut },
  ];
}

const LIBELLES_PARTIE: Record<string, string> = { echauffement: 'Échauffement', corps: 'Corps de séance', recuperation: 'Retour au calme' };

function duree(s: number): string {
  const total = Math.round(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
  return sec ? `${m}'${String(sec).padStart(2, '0')}"` : `${m}'`;
}

function distance(m: number): string {
  return m >= 1000
    ? `${(m / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} km`
    : `${Math.round(m / 10) * 10} m`;
}

// Pas des graduations de temps (minutes) : au plus ~8 reperes.
function pasGraduation(totalS: number): number {
  const minutes = totalS / 60;
  return [1, 2, 5, 10, 15, 20, 30, 60].find((p) => minutes / p <= 8) ?? 120;
}

/**
 * Visualisation d'une seance : chaque etape est une barre dont la largeur
 * est sa duree et la hauteur son intensite (% de VMA), dans la couleur de
 * sa zone ; les recuperations sont plus pales. L'echauffement et le retour
 * au calme du modele sont sur fond grise. Survol : detail de l'etape.
 */
export default function ProfilSeance({
  notations,
  vma,
  table,
  hauteur = 140,
}: {
  notations: { echauffement?: string | null; corps: string; recuperation?: string | null };
  vma: number;
  table: ZoneRow[];
  hauteur?: number;
}) {
  const idDegrade = React.useId().replace(/:/g, ''); // id utilisable dans url(#...)
  const resultat = React.useMemo(
    () => profilSeance(notations, vma, table),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notations.echauffement, notations.corps, notations.recuperation, vma, table],
  );

  if ('erreur' in resultat) {
    return (
      <Typography variant="body2" color="text.secondary">
        Visualisation indisponible : {resultat.erreur}
      </Typography>
    );
  }
  const profil: Profil = resultat;
  const total = profil.dureeTotaleS;

  // Echelle verticale : de 45 % de VMA (bas) a un peu au-dessus de l'effort le plus intense.
  const pctMax = Math.max(1.0, ...profil.segments.flatMap((s: SegmentProfil) => [s.pctVMA, s.pctVMAFin ?? 0, s.pctVMADebut ?? 0])) + 0.03;
  const PCT_BAS = 0.45;
  const hauteurPct = (pct: number) => Math.max(4, Math.min(100, ((pct - PCT_BAS) / (pctMax - PCT_BAS)) * 100));

  // Lignes de repere : une par zone d'effort presente.
  const reperes: Repere[] = Array.from(
    new Map(
      profil.segments
        .filter((s: SegmentProfil) => !s.recup)
        .flatMap(zonesDe)
        .map((r: Repere) => [r.code, r] as [string, Repere]),
    ).values(),
  ).sort((a: Repere, b: Repere) => b.pctVMA - a.pctVMA);

  const pas = pasGraduation(total) * 60;
  const graduations: number[] = [];
  for (let t = 0; t <= total + 1; t += pas) graduations.push(t);

  return (
    <Box sx={{ pl: 0, pr: 0 }}>
      <Box sx={{ position: 'relative', height: hauteur, borderBottom: 1, borderColor: 'divider' }}>
        <svg
          viewBox={`0 0 ${total} 100`}
          preserveAspectRatio="none"
          width="100%"
          height="100%"
          style={{ position: 'absolute', inset: 0, display: 'block' }}
          role="img"
          aria-label={`Profil de la séance : ${duree(total)}, ${distance(profil.distanceTotaleM)}`}
        >
          {profil.parties
            .filter((p: PartiePlacee) => p.partie !== 'corps')
            .map((p: PartiePlacee) => (
              <rect key={p.partie} x={p.debutS} y={0} width={p.finS - p.debutS} height={100} fill="rgba(15, 23, 42, 0.05)" />
            ))}
          {/* Effort progressif : degrade de la couleur de depart a celle d'arrivee */}
          <defs>
            {profil.segments.map((s: SegmentProfil, i: number) =>
              s.codeFin ? (
                <linearGradient key={i} id={`${idDegrade}-${i}`} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor={couleurPourCode(s.code)} />
                  <stop offset="1" stopColor={couleurPourCode(s.codeFin)} />
                </linearGradient>
              ) : null,
            )}
          </defs>
          {reperes.map((r: Repere) => (
            <line
              key={r.code}
              x1={0}
              x2={total}
              y1={100 - hauteurPct(r.pctVMA)}
              y2={100 - hauteurPct(r.pctVMA)}
              stroke={couleurPourCode(r.code)}
              strokeOpacity={0.5}
              strokeDasharray="4 4"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {profil.segments.map((s: SegmentProfil, i: number) => {
            const h = hauteurPct(s.pctVMA);
            const forme = s.codeFin ? (
              <polygon
                points={`${s.debutS},100 ${s.debutS},${100 - hauteurPct(s.pctVMADebut ?? s.pctVMA)} ${s.debutS + s.dureeS},${100 - hauteurPct(s.pctVMAFin ?? s.pctVMA)} ${s.debutS + s.dureeS},100`}
                fill={`url(#${idDegrade}-${i})`}
                fillOpacity={0.95}
                stroke="#fff"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                style={{ cursor: 'default' }}
              />
            ) : (
              <rect
                x={s.debutS}
                y={100 - h}
                width={s.dureeS}
                height={h}
                fill={s.recup ? '#b0bec5' : couleurPourCode(s.code)}
                fillOpacity={s.recup ? 0.7 : 0.95}
                stroke="#fff"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                style={{ cursor: 'default' }}
              />
            );
            return (
              <Tooltip
                key={i}
                followCursor
                title={
                  <Box>
                    <b>{s.recup ? 'Récupération' : s.nomZone}</b>
                    <Box>{duree(s.dureeS)} · {distance(s.distanceM)}</Box>
                    {s.codeFin ? (
                      <Box>
                        {allureAuPct(vma, s.pctVMADebut ?? s.pctVMA)} → {allureAuPct(vma, s.pctVMAFin ?? s.pctVMA)} (moyenne {allureAuPct(vma, s.pctVMA)})
                      </Box>
                    ) : (
                      <Box>{allureAuPct(vma, s.pctVMA)} · {Math.round(s.pctVMA * 100)} % VMA</Box>
                    )}
                    <Box sx={{ opacity: 0.8 }}>{LIBELLES_PARTIE[s.partie]} · à {duree(s.debutS)}</Box>
                  </Box>
                }
              >
                {forme}
              </Tooltip>
            );
          })}
        </svg>
        {/* Libelles des parties du modele (en haut) */}
        {profil.parties.length > 1 &&
          profil.parties.map((p: PartiePlacee) => (
            <Typography
              key={p.partie}
              variant="caption"
              sx={{
                position: 'absolute',
                top: 2,
                left: `${(100 * p.debutS) / total}%`,
                width: `${(100 * (p.finS - p.debutS)) / total}%`,
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: '0.65rem',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                pointerEvents: 'none',
              }}
            >
              {LIBELLES_PARTIE[p.partie]}
            </Typography>
          ))}
        {/* Allure de chaque zone (lignes de repere) */}
        {reperes.map((r: Repere) => (
          <Typography
            key={r.code}
            variant="caption"
            sx={{
              position: 'absolute',
              right: 2,
              top: `${100 - hauteurPct(r.pctVMA)}%`,
              transform: 'translateY(-100%)',
              fontSize: '0.65rem',
              lineHeight: 1.2,
              color: couleurPourCode(r.code),
              fontWeight: 600,
              bgcolor: (t) => alpha(t.palette.background.paper, 0.8),
              px: 0.5,
              borderRadius: 0.5,
              pointerEvents: 'none',
            }}
          >
            {r.nomZone} · {allureAuPct(vma, r.pctVMA)}
          </Typography>
        ))}
      </Box>
      {/* Axe du temps */}
      <Box sx={{ position: 'relative', height: 18 }}>
        {graduations.map((t) => (
          <Typography
            key={t}
            variant="caption"
            sx={{
              position: 'absolute',
              top: 2,
              left: `${(100 * t) / total}%`,
              transform: t === 0 ? 'none' : t >= total - pas / 2 ? 'translateX(-100%)' : 'translateX(-50%)',
              fontSize: '0.65rem',
              color: 'text.secondary',
            }}
          >
            {duree(t)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
